import type { JSONContent } from '@tiptap/core';

// Convert TipTap JSON document to Markdown string
export function toMarkdown(doc: JSONContent): string {
  if (!doc || doc.type !== 'doc') return '';
  return renderNodes(doc.content || [], 0);
}

function renderNodes(nodes: JSONContent[], depth: number): string {
  return nodes.map((node) => renderNode(node, depth)).join('\n\n');
}

function renderNode(node: JSONContent, depth: number): string {
  const indent = '    '.repeat(depth);

  switch (node.type) {
    case 'doc':
      return renderNodes(node.content || [], depth);

    case 'paragraph': {
      const text = renderInline(node.content || []);
      return text;
    }

    case 'heading': {
      const level = node.attrs?.['level'] as number || 1;
      const text = renderInline(node.content || []);
      return `${'#'.repeat(level)} ${text}`;
    }

    case 'bulletList': {
      const items = (node.content || []).map((item) => renderListItem(item, depth, false));
      return items.join('\n');
    }

    case 'orderedList': {
      const items = (node.content || []).map((item, idx) =>
        renderListItem(item, depth, true, idx + 1)
      );
      return items.join('\n');
    }

    case 'listItem': {
      return renderListItem(node, depth, false);
    }

    case 'blockquote': {
      const inner = renderNodes(node.content || [], depth);
      return inner
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
    }

    case 'codeBlock': {
      const lang = node.attrs?.['language'] as string || '';
      const code = renderText(node.content || []);
      return `\`\`\`${lang}\n${code}\n\`\`\``;
    }

    case 'horizontalRule':
      return '---';

    case 'hardBreak':
      return '  \n';

    case 'table':
      return renderTable(node);

    case 'image': {
      const src = node.attrs?.['src'] as string || '';
      const alt = node.attrs?.['alt'] as string || '';
      const width = node.attrs?.['width'] as number | null;
      if (width) {
        return `![${alt}](${src}){ width=${width}; loading=lazy }`;
      }
      return `![${alt}](${src})`;
    }

    case 'admonition': {
      const type = node.attrs?.['type'] as string || 'note';
      const title = node.attrs?.['title'] as string || '';
      const collapsible = node.attrs?.['collapsible'] as boolean || false;
      const prefix = collapsible ? '???' : '!!!';
      const titleStr = title ? ` "${title}"` : '';
      const header = `${prefix} ${type}${titleStr}`;
      const body = renderNodes(node.content || [], 0)
        .split('\n')
        .map((l) => (l.trim() === '' ? '' : `    ${l}`))
        .join('\n');
      return `${header}\n${body}`;
    }

    case 'mermaidBlock': {
      const code = node.attrs?.['code'] as string || '';
      return `\`\`\`mermaid\n${code}\n\`\`\``;
    }

    case 'imageCaption': {
      const captionText = renderInline(node.content || []);
      return `/// caption\n${captionText}\n///`;
    }

    default:
      // Fallback: try to render as inline text
      if (node.content) {
        return renderInline(node.content);
      }
      return '';
  }
}

function renderListItem(
  node: JSONContent,
  depth: number,
  ordered: boolean,
  num = 1
): string {
  const children = node.content || [];
  if (children.length === 0) return ordered ? `${num}. ` : '- ';

  const indent = '    '.repeat(depth);
  const bullet = ordered ? `${num}. ` : '- ';

  const firstChild = children[0];
  let firstLine = '';
  const restLines: string[] = [];

  if (firstChild?.type === 'paragraph') {
    firstLine = renderInline(firstChild.content || []);
  }

  for (let i = 1; i < children.length; i++) {
    const child = children[i];
    const childText = renderNode(child, depth + 1);
    restLines.push(childText);
  }

  let result = `${indent}${bullet}${firstLine}`;
  if (restLines.length > 0) {
    result += '\n' + restLines.map((l) => `    ${indent}${l}`).join('\n');
  }

  return result;
}

function renderTable(node: JSONContent): string {
  const rows = node.content || [];
  if (rows.length === 0) return '';

  const headerRow = rows[0];
  const bodyRows = rows.slice(1);

  const headerCells = (headerRow.content || []).map((cell) =>
    renderInline(cell.content?.[0]?.content || [])
  );

  const separator = headerCells.map(() => '---').join(' | ');
  const header = `| ${headerCells.join(' | ')} |`;
  const sep = `| ${separator} |`;

  const body = bodyRows.map((row) => {
    const cells = (row.content || []).map((cell) =>
      renderInline(cell.content?.[0]?.content || [])
    );
    return `| ${cells.join(' | ')} |`;
  });

  return [header, sep, ...body].join('\n');
}

function renderInline(nodes: JSONContent[]): string {
  return nodes.map(renderInlineNode).join('');
}

function renderInlineNode(node: JSONContent): string {
  if (node.type === 'hardBreak') return '  \n';

  if (node.type === 'image') {
    const src = node.attrs?.['src'] as string || '';
    const alt = node.attrs?.['alt'] as string || '';
    const width = node.attrs?.['width'] as number | null;
    if (width) {
      return `![${alt}](${src}){ width=${width}; loading=lazy }`;
    }
    return `![${alt}](${src})`;
  }

  if (node.type !== 'text') {
    return renderNode(node, 0);
  }

  const text = node.text || '';
  const marks = node.marks || [];

  let result = text;

  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        result = `**${result}**`;
        break;
      case 'italic':
        result = `*${result}*`;
        break;
      case 'code':
        result = `\`${result}\``;
        break;
      case 'strike':
        result = `~~${result}~~`;
        break;
      case 'underline':
        // Underline has no standard Markdown equivalent; use HTML
        result = `<u>${result}</u>`;
        break;
      case 'link': {
        const href = mark.attrs?.['href'] as string || '';
        result = `[${result}](${href})`;
        break;
      }
      case 'superscript':
        result = `<sup>${result}</sup>`;
        break;
      case 'subscript':
        result = `<sub>${result}</sub>`;
        break;
    }
  }

  return result;
}

function renderText(nodes: JSONContent[]): string {
  return nodes.map((n) => n.text || '').join('');
}
